/* =========================================================================
   GTKY memory cache — the client-side "companion memory" (workstream I). Answers
   to Get-To-Know-You prompts are cached by MemoryKey at
   localStorage['catholics.gtky.<key>']. Character line defs read this cache
   (via the registry) to select personalised lines with a generic fallback.
   Local for everyone; accounts additionally sync to the server GTKY bank.
   ========================================================================= */
const PREFIX = 'catholics.gtky.';

/** Stored as the answer when the player taps "Prefer not to say" (PLAN-gameplay-v2
    §6 respect rule). It occupies the key so the question is NEVER re-asked, but
    it is NOT an answer: getLine() treats it as unsatisfied (registry.ts) and it
    is never mirrored to the server bank (gtky.ts). Underscore-wrapped so it can
    never collide with a real answer, exactly like NAME_SKIP_SENTINEL. */
export const GTKY_DECLINE_SENTINEL = '__declined__';

/** True when a cached value is a real answer rather than a decline. PURE. */
export function isRealAnswer(value: string | null | undefined): boolean {
  return value != null && value !== GTKY_DECLINE_SENTINEL;
}

export function rememberGtky(key: string, value: string): void {
  try {
    localStorage.setItem(PREFIX + key, value);
  } catch {
    /* private mode / eviction — memory simply won't persist */
  }
}

/** Forget one cached answer (logout hygiene: e.g. 'player_name' must not greet
    the previous owner of a shared device after sign-out). */
export function forgetGtky(key: string): void {
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {
    /* noop */
  }
}

export function recallGtky(key: string): string | null {
  try {
    return localStorage.getItem(PREFIX + key);
  } catch {
    return null;
  }
}

/** All cached GTKY answers as a plain { key: value } map (used by getLine). */
export function allMemory(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) {
        const v = localStorage.getItem(k);
        if (v != null) out[k.slice(PREFIX.length)] = v;
      }
    }
  } catch {
    /* storage unavailable — no memory, generic lines only */
  }
  return out;
}
